import { clamp } from 'lodash-es';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { filter, fromEvent, tap } from 'rxjs';

import { useCustomEventEmitter, useSubscriptions } from '@/hook';

import { useVBaseTrack } from '../VBaseTrack';
import { useVSliderContext } from '../VSlider/hook';
import {
  ARIA_LABEL,
  FRAME_SLIDER_SCALE,
  FRAME_SLIDER_SCALE_CONFIG,
} from './constant';
import { VFrameSliderContext } from './context';
import type {
  FrameSliderRange,
  VFrameSliderEventHandlerMap,
  VFrameSliderProps,
} from './type';
import { alignThumbToCenter, getStepConfig } from './util';

type VFrameSliderProviderProps = PropsWithChildren<
  Required<Pick<VFrameSliderProps, 'value' | 'scale'>> &
    Pick<
      VFrameSliderProps,
      | 'autoTracking'
      | 'range'
      | 'variant'
      | 'onChange'
      | 'onRangeChange'
      | 'onScaleChange'
    >
>;

export const VFrameSliderContextProvider = ({
  children,

  autoTracking,
  range: initialRange = null,
  scale: initialScale,
  value: initialValue,
  variant,
  onChange,
  onRangeChange,
  onScaleChange,
}: VFrameSliderProviderProps) => {
  const { setStep } = useVBaseTrack();
  const { disabled, max, min, readOnly, $container } = useVSliderContext();

  const [value, _setValue] = useState(initialValue);
  const [range, _setRange] = useState<Nullish<FrameSliderRange>>(initialRange);
  const [scale, _setScale] = useState<FRAME_SLIDER_SCALE>(initialScale);
  const $thumbRef = useRef<HTMLDivElement>(null);

  const size = max - min + 1;

  const { eventEmitter } = useCustomEventEmitter<VFrameSliderEventHandlerMap>({
    onChange: onChange ?? (() => {}),
    onRangeChange: onRangeChange ?? (() => {}),
    onScaleChange: onScaleChange ?? (() => {}),
  });

  const setValue = (
    newValue: number,
    trigger?: 'thumb' | 'track' | 'marker',
  ) => {
    _setValue(newValue);

    if (onChange) {
      eventEmitter.current.emit('change', { value: newValue }, { trigger });
    }
  };

  const setRange = (range: FrameSliderRange) => {
    _setRange(range);

    if (onRangeChange) {
      eventEmitter.current.emit('rangeChange', { value: range });
    }
  };

  const setScale = (newScale: FRAME_SLIDER_SCALE) => {
    let timelineScaleThreshold = FRAME_SLIDER_SCALE.SCALE_1;

    for (const key in FRAME_SLIDER_SCALE_CONFIG) {
      const k = +key as FRAME_SLIDER_SCALE;
      const { threshold } = FRAME_SLIDER_SCALE_CONFIG[k];

      if (size >= threshold) timelineScaleThreshold = k;
    }

    newScale = clamp(
      newScale,
      FRAME_SLIDER_SCALE.SCALE_1,
      timelineScaleThreshold,
    ) as FRAME_SLIDER_SCALE;

    _setScale(newScale);

    if (onScaleChange) {
      eventEmitter.current.emit('scaleChange', { scale: newScale });
    }
  };

  const contextValue = {
    disabled,
    eventEmitter: eventEmitter.current,
    max,
    min,
    range,
    readOnly,
    scale,
    value,
    variant,
    $thumbRef,

    setValue,
    setRange,
    setScale,
  };

  useEffect(() => {
    setStep(getStepConfig(scale).step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  useSubscriptions(
    () => [
      fromEvent<WheelEvent>(window, 'wheel', { passive: false })
        .pipe(
          filter(
            e =>
              (e.target === $container.current ||
                (e.target as HTMLElement).closest(
                  `[aria-label="${ARIA_LABEL.FRAME_SLIDER}"]`,
                ) === $container.current) &&
              !e.shiftKey,
          ),
          tap(e => {
            e.preventDefault();
            setScale(
              (e.deltaY > 0 ? scale + 1 : scale - 1) as FRAME_SLIDER_SCALE,
            );
            setTimeout(() => alignThumbToCenter({ $container, $thumbRef }), 32);
          }),
        )
        .subscribe(),
    ],
    [scale],
  );

  useEffect(() => {
    if (!autoTracking) return;

    alignThumbToCenter({ $container, $thumbRef });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTracking, max, min, value]);

  useEffect(() => {
    if (initialValue !== value) setValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return (
    <VFrameSliderContext.Provider value={contextValue}>
      {children}
    </VFrameSliderContext.Provider>
  );
};

VFrameSliderContextProvider.displayName = 'VFrameSliderContextProvider';
